import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import {
  requestCustomConceptOverview,
  requestCustomConceptTask,
} from 'services/api/customConcept';

const initialState = {
  overview: {},
  fetchOverview: false,
  errorOverview: null,
  // keyed by the group/task id so several groups can stay expanded at once
  tasks: {},
  fetchTasks: false,
  errorTasks: null,
};

const customConcept = createSlice({
  name: 'customConcept',
  initialState,
  reducers: {
    initCustomConceptOverview: (state) => {
      state.fetchOverview = true;
      state.errorOverview = null;
    },
    successCustomConceptOverview: (state, { payload }) => {
      state.overview = payload;
      state.fetchOverview = false;
      state.errorOverview = null;
    },
    errorCustomConceptOverview: (state, { payload }) => {
      state.fetchOverview = false;
      state.errorOverview = { message: payload };
    },
    initCustomConceptTask: (state) => {
      state.fetchTasks = true;
      state.errorTasks = null;
    },
    successCustomConceptTask: (state, { payload }) => {
      state.tasks[payload.id] = payload.data;
      state.fetchTasks = false;
      state.errorTasks = null;
    },
    errorCustomConceptTask: (state, { payload }) => {
      state.fetchTasks = false;
      state.errorTasks = { message: payload };
    },
    // Mirrors `successUpdateKey` in the projects slice for the inline popover
    // edits. Tasks sit under format > source here rather than being indexed by
    // channel, so the matching task is found by walking that hierarchy.
    successCustomConceptUpdateKey: (state, { payload }) => {
      const {
        params1: { id, key, value },
        params2: { data },
        response,
      } = payload;

      const changes = {
        task_status: { status_name: data, status_id: value },
        task_due_date: { due_date: response?.due_date },
        task_delivery_date: { delivery_date: response?.delivery_date },
        task_assignees: { assignees: response },
        task_watchers: { watchers: response },
      }[key];

      if (_.isUndefined(changes)) return;

      _.forEach(state.tasks, (formats) =>
        _.forEach(formats, (sources) =>
          _.forEach(sources, (source) =>
            _.forEach(source?.tasks, (task) => {
              if (task?.id === id) {
                _.assign(task, changes);
                return;
              }

              const subtask = _.find(task?.subtasks, { id });

              subtask && _.assign(subtask, changes);
            })
          )
        )
      );
    },
    resetCustomConcept: () => initialState,
  },
});

export const {
  initCustomConceptOverview,
  successCustomConceptOverview,
  errorCustomConceptOverview,
  initCustomConceptTask,
  successCustomConceptTask,
  errorCustomConceptTask,
  successCustomConceptUpdateKey,
  resetCustomConcept,
} = customConcept.actions;

export const getCustomConceptOverview =
  (conceptId, withTask = true) =>
  async (dispatch) => {
    dispatch(initCustomConceptOverview());

    const { success, message, data } = await requestCustomConceptOverview(
      conceptId,
      withTask
    );

    success
      ? dispatch(successCustomConceptOverview(data))
      : dispatch(errorCustomConceptOverview(message));

    return data;
  };

export const getCustomConceptTask = (taskId) => async (dispatch) => {
  dispatch(initCustomConceptTask());

  const { success, message, data } = await requestCustomConceptTask(taskId);

  success
    ? dispatch(successCustomConceptTask({ id: taskId, data }))
    : dispatch(errorCustomConceptTask(message));

  return data;
};

export default customConcept.reducer;
